/**
 * SquadCup · Admin App (Andrea 2026-10-08): si crea un'edizione, si
 * scelgono le squadre fra i vincitori delle leghe (la regola suggerisce:
 * sotto 8 leghe campione + coppa, da 8 in su solo i campioni), si assegnano
 * le giornate di Serie A ai turni, sorteggio, poi "Calcola" dopo ogni
 * giornata calcolata nelle leghe.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  type Candidato,
  type SquadCupDettaglio,
  useCalcolaSquadCup,
  useCreaEdizione,
  useEliminaEdizione,
  useGiornateSA,
  useImpostaPartecipanti,
  useSorteggio,
  useSquadCupCandidati,
  useSquadCupEdizione,
  useSquadCupEdizioni,
} from "../../hooks/useSquadCup";
import { TeamBadge } from "../common/TeamBadge";
import { etichetta } from "../common/etichette";
import { MobileShell } from "../shell/MobileShell";
import { Tabellone } from "../squadcup/Tabellone";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";

const INPUT =
  "w-full rounded-lg bg-bg border border-primary-soft/50 px-3 py-2 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:border-accent";
const LABEL = "block text-xs uppercase tracking-wider text-fg-muted mb-1";

function errMsg(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err.response?.data?.detail ?? err.message ?? "errore";
}

function nTurni(n: number): number {
  return n >= 2 && (n & (n - 1)) === 0 ? Math.log2(n) : 0;
}

export function SquadCupAdminPage() {
  const { user } = useAuth();
  const { data: edizioni } = useSquadCupEdizioni();
  const [sceltaId, setSceltaId] = useState<string | null>(null);
  const idCorrente = sceltaId ?? edizioni?.[0]?.id ?? null;
  const { data: ed } = useSquadCupEdizione(idCorrente);
  const [nuova, setNuova] = useState(false);

  if (!user?.is_admin_app) {
    return (
      <MobileShell logoSuffix="SQUADCUP">
        <div className="card text-sm text-fg-muted">
          {tr("La SquadCup la gestisce l'amministratore dell'app.")}
        </div>
      </MobileShell>
    );
  }

  return (
    <MobileShell logoSuffix="SQUADCUP">
      <div className="card space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
            {tr("SquadCup · gestione")}
          </div>
          <div className="flex gap-2">
            <Link to="/squadcup" className="text-[11px] text-accent hover:underline">
              {tr("Vedi come la vedono tutti ›")}
            </Link>
            <button
              type="button"
              className="btn-primary text-xs px-3 py-1.5"
              onClick={() => setNuova(true)}
            >
              {tr("+ Nuova edizione")}
            </button>
          </div>
        </div>
        <div className="text-[11px] text-fg-muted">
          Regole: almeno 8 squadre; sotto le 8 leghe entrano campione e vincitore di coppa di ogni
          lega, da 8 leghe in su solo i campioni. Si gioca su giornate di Serie A a tua scelta; ogni
          squadra conta i fantapunti fatti nella propria lega. Il trofeo va in bacheca.
        </div>
        {edizioni && edizioni.length > 0 && (
          <div className="flex gap-2 flex-wrap pt-1">
            {edizioni.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => setSceltaId(e.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                  e.id === idCorrente
                    ? "bg-accent text-ink border-accent"
                    : "border-primary/40 text-fg-muted hover:text-fg"
                }`}
              >
                {e.nome} · {etichetta(e.stato)}
              </button>
            ))}
          </div>
        )}
      </div>

      {nuova && (
        <NuovaEdizione
          onChiudi={() => setNuova(false)}
          onCreata={(id) => {
            setSceltaId(id);
            setNuova(false);
          }}
        />
      )}

      {ed && ed.stato === "bozza" && <Bozza ed={ed} />}
      {ed && ed.stato !== "bozza" && <InCorso ed={ed} />}
    </MobileShell>
  );
}

function NuovaEdizione({
  onChiudi,
  onCreata,
}: {
  onChiudi: () => void;
  onCreata: (id: string) => void;
}) {
  const { data: cand } = useSquadCupCandidati(true);
  const crea = useCreaEdizione();
  const [nome, setNome] = useState("");
  const [stagione, setStagione] = useState("");
  const [regola, setRegola] = useState<"campioni" | "campioni_e_coppe">("campioni_e_coppe");
  const [diProva, setDiProva] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  useEffect(() => {
    if (cand?.stagione_serie_a_corrente && !stagione) {
      setStagione(cand.stagione_serie_a_corrente);
      setNome(tr("SquadCup {{v1}}", { v1: cand.stagione_serie_a_corrente }));
    }
    if (cand) setRegola(cand.regola_consigliata);
  }, [cand, stagione]);

  const salva = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrore(null);
    try {
      const r = await crea.mutateAsync({
        nome,
        stagione_serie_a: stagione.trim(),
        regola,
        di_prova: diProva,
      });
      onCreata(r.id);
    } catch (err) {
      setErrore(errMsg(err));
    }
  };

  return (
    <form onSubmit={salva} className="card space-y-3">
      <div className="font-display text-lg text-fg">{tr("Nuova edizione")}</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={LABEL} htmlFor="sc-nome">
            {tr("Nome")}
          </label>
          <input
            id="sc-nome"
            className={INPUT}
            value={nome}
            onChange={(e) => setNome(e.target.value)}
          />
        </div>
        <div>
          <label className={LABEL} htmlFor="sc-stagione">
            {tr("Stagione Serie A")}
          </label>
          <input
            id="sc-stagione"
            className={INPUT}
            value={stagione}
            placeholder="2026-27"
            onChange={(e) => setStagione(e.target.value)}
          />
        </div>
      </div>
      <div>
        <div className={LABEL}>{tr("Chi si qualifica")}</div>
        <div className="flex gap-2 flex-wrap">
          {(
            [
              ["campioni_e_coppe", tr("Campione + vincitore di coppa")],
              ["campioni", tr("Solo i campioni")],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setRegola(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border ${
                regola === k
                  ? "bg-accent text-ink border-accent"
                  : "border-primary/40 text-fg-muted"
              }`}
            >
              {label}
              {cand?.regola_consigliata === k && tr(" · consigliata")}
            </button>
          ))}
        </div>
        {cand && (
          <div className="text-[11px] text-fg-muted mt-1">
            Leghe attive: {cand.n_leghe}. Consigliata:{" "}
            {cand.regola_consigliata === "campioni"
              ? tr("solo i campioni")
              : tr("campione + coppa")}{" "}
            (la soglia è {cand.min_squadre} leghe).
          </div>
        )}
      </div>
      <label className="flex items-center gap-2 text-xs text-fg">
        <input type="checkbox" checked={diProva} onChange={(e) => setDiProva(e.target.checked)} />
        {tr("Edizione di prova (ammette meno di 8 squadre, si può cancellare)")}
      </label>
      {errore && <div className="text-xs text-danger">{errore}</div>}
      <div className="flex gap-2 justify-end">
        <button type="button" className="btn-ghost text-sm" onClick={onChiudi}>
          {tr("Annulla")}
        </button>
        <button type="submit" className="btn-primary text-sm" disabled={crea.isPending || !nome}>
          {tr("Crea")}
        </button>
      </div>
    </form>
  );
}

function Bozza({ ed }: { ed: SquadCupDettaglio }) {
  const { data: cand } = useSquadCupCandidati(true);
  const { data: giornate } = useGiornateSA(ed.stagione_serie_a);
  const imposta = useImpostaPartecipanti();
  const sorteggio = useSorteggio();
  const elimina = useEliminaEdizione();
  const [errore, setErrore] = useState<string | null>(null);

  // selezione: parte dai partecipanti salvati, altrimenti dai candidati secondo la regola
  const [scelte, setScelte] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (ed.partecipanti.length > 0) {
      setScelte(new Map(ed.partecipanti.map((p) => [p.squadra.id, p.titolo])));
    } else if (cand) {
      const m = new Map<string, string>();
      for (const c of cand.candidati) {
        if (ed.regola === "campioni" && c.titolo !== "campione") continue;
        m.set(c.squadra_id, c.titolo);
      }
      setScelte(m);
    }
  }, [ed.id, ed.partecipanti, ed.regola, cand]);

  const toggle = (c: Candidato) => {
    setScelte((prev) => {
      const next = new Map(prev);
      if (next.has(c.squadra_id)) next.delete(c.squadra_id);
      else next.set(c.squadra_id, c.titolo);
      return next;
    });
  };

  const n = scelte.size;
  const turni = nTurni(n);
  const [giornateTurni, setGiornateTurni] = useState<{ andata: number; ritorno: number | null }[]>(
    [],
  );
  useEffect(() => {
    setGiornateTurni((prev) => {
      if (prev.length === turni) return prev;
      return Array.from({ length: turni }, (_, i) => prev[i] ?? { andata: 0, ritorno: null });
    });
  }, [turni]);

  const nomiTurni = useMemo(() => {
    const nomi: string[] = [];
    let squadre = n;
    for (let i = 0; i < turni; i++) {
      const partite = squadre / 2;
      nomi.push(
        {
          1: tr("Finale"),
          2: tr("Semifinali"),
          4: tr("Quarti"),
          8: tr("Ottavi"),
          16: tr("Sedicesimi"),
        }[partite] ?? tr("Turno {{v1}}", { v1: i + 1 }),
      );
      squadre /= 2;
    }
    return nomi;
  }, [n, turni]);

  const salvaPartecipanti = async () => {
    setErrore(null);
    try {
      await imposta.mutateAsync({
        id: ed.id,
        partecipanti: [...scelte.entries()].map(([squadra_id, titolo]) => ({ squadra_id, titolo })),
      });
    } catch (err) {
      setErrore(errMsg(err));
    }
  };

  const faiSorteggio = async () => {
    setErrore(null);
    if (giornateTurni.some((g) => !g.andata)) {
      setErrore(tr("Indica la giornata di andata per ogni turno"));
      return;
    }
    if (
      !confirm(
        tr("Sorteggio con {{v1}} squadre e {{v2}} turni. Dopo non si cambiano più. Procedo?", {
          v1: n,
          v2: turni,
        }),
      )
    )
      return;
    try {
      await imposta.mutateAsync({
        id: ed.id,
        partecipanti: [...scelte.entries()].map(([squadra_id, titolo]) => ({ squadra_id, titolo })),
      });
      await sorteggio.mutateAsync({ id: ed.id, giornate: giornateTurni });
    } catch (err) {
      setErrore(errMsg(err));
    }
  };

  const dataG = (num: number) => {
    const g = giornate?.find((x) => x.numero === num);
    return g?.prima_partita
      ? new Date(g.prima_partita).toLocaleDateString(localeCorrente(), {
          day: "numeric",
          month: "short",
        })
      : "";
  };

  return (
    <>
      <div className="card space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="font-display text-lg text-fg">
            {ed.nome} <span className="text-xs text-fg-muted font-sans">{tr("· bozza")}</span>
          </div>
          <button
            type="button"
            className="text-[11px] text-danger hover:underline"
            onClick={async () => {
              if (!confirm(tr('Eliminare l\'edizione "{{v1}}"?', { v1: ed.nome }))) return;
              await elimina.mutateAsync(ed.id);
            }}
          >
            {tr("Elimina")}
          </button>
        </div>

        <div>
          <div className={LABEL}>
            Squadre ({n}) — dai vincitori dell'ultima stagione conclusa di ogni lega
          </div>
          {cand && cand.candidati.length === 0 && (
            <div className="text-xs text-fg-muted">
              {tr("Nessuna lega ha ancora una stagione conclusa: niente campioni da qualificare.")}
            </div>
          )}
          <div className="grid gap-2 sm:grid-cols-2">
            {cand?.candidati.map((c) => {
              const on = scelte.has(c.squadra_id);
              return (
                <button
                  key={c.squadra_id}
                  type="button"
                  onClick={() => toggle(c)}
                  className={`flex items-center gap-2 rounded-card border p-2 text-left transition ${
                    on
                      ? "border-accent bg-accent/10"
                      : "border-primary/30 bg-primary-deep/30 opacity-70"
                  }`}
                >
                  <TeamBadge
                    nome={c.squadra_nome}
                    logoDataUrl={c.logo_data_url}
                    size={28}
                    variant="home"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-fg truncate">{c.squadra_nome}</div>
                    <div className="text-[10px] text-fg-muted truncate">
                      {c.lega_nome} · {c.titolo === "campione" ? "campione" : "coppa"}{" "}
                      {c.stagione_anno}
                    </div>
                  </div>
                  <span className="text-xs">{on ? "✅" : "⬜"}</span>
                </button>
              );
            })}
          </div>
          <div className="text-[11px] text-fg-muted mt-1">
            {turni === 0
              ? tr("Con {{v1}} squadre il tabellone non torna: servono 2, 4, 8, 16 o 32.", {
                  v1: n,
                })
              : tr("{{v1}} squadre → {{v2}} turni ({{v3}}).", {
                  v1: n,
                  v2: turni,
                  v3: nomiTurni.join(", "),
                })}
            {n < 8 &&
              n > 0 &&
              !ed.di_prova &&
              tr(" Sotto le 8 squadre serve un'edizione di prova.")}
          </div>
          <button
            type="button"
            className="btn-ghost text-xs mt-2"
            onClick={() => void salvaPartecipanti()}
            disabled={imposta.isPending}
          >
            {tr("Salva partecipanti")}
          </button>
        </div>

        {turni > 0 && (
          <div>
            <div className={LABEL}>Giornate di Serie A per turno (ritorno vuoto = gara secca)</div>
            <div className="space-y-2">
              {giornateTurni.map((g, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto_auto] items-center gap-2 text-sm">
                  <div className="text-fg">{nomiTurni[i]}</div>
                  <label className="flex items-center gap-1 text-xs text-fg-muted">
                    {"andata"}
                    <input
                      type="number"
                      min={1}
                      max={38}
                      value={g.andata || ""}
                      onChange={(e) =>
                        setGiornateTurni((prev) =>
                          prev.map((x, k) =>
                            k === i ? { ...x, andata: Number(e.target.value) } : x,
                          ),
                        )
                      }
                      className="w-16 rounded bg-bg border border-primary-soft/50 px-2 py-1 text-fg"
                    />
                    <span className="w-14 text-[10px]">{dataG(g.andata)}</span>
                  </label>
                  <label className="flex items-center gap-1 text-xs text-fg-muted">
                    {"ritorno"}
                    <input
                      type="number"
                      min={1}
                      max={38}
                      value={g.ritorno ?? ""}
                      onChange={(e) =>
                        setGiornateTurni((prev) =>
                          prev.map((x, k) =>
                            k === i
                              ? { ...x, ritorno: e.target.value ? Number(e.target.value) : null }
                              : x,
                          ),
                        )
                      }
                      className="w-16 rounded bg-bg border border-primary-soft/50 px-2 py-1 text-fg"
                    />
                    <span className="w-14 text-[10px]">{g.ritorno ? dataG(g.ritorno) : ""}</span>
                  </label>
                </div>
              ))}
            </div>
            {errore && <div className="text-xs text-danger mt-2">{errore}</div>}
            <button
              type="button"
              className="btn-primary w-full mt-3 disabled:opacity-50"
              onClick={() => void faiSorteggio()}
              disabled={sorteggio.isPending || imposta.isPending}
            >
              {tr("🎲 Sorteggio e via")}
            </button>
          </div>
        )}
        {turni === 0 && errore && <div className="text-xs text-danger">{errore}</div>}
      </div>
    </>
  );
}

function InCorso({ ed }: { ed: SquadCupDettaglio }) {
  const calcola = useCalcolaSquadCup();
  const elimina = useEliminaEdizione();
  const [esito, setEsito] = useState<string | null>(null);

  const onCalcola = async () => {
    setEsito(null);
    try {
      const r = await calcola.mutateAsync(ed.id);
      const rep = r.report;
      setEsito(
        tr("Decise {{v1}} sfide, {{v2}} in attesa delle giornate", {
          v1: rep.partite_decise,
          v2: rep.in_attesa,
        }) +
          (rep.turni_aperti ? tr(", aperto il turno successivo") : "") +
          (rep.conclusa ? tr(". Edizione conclusa!") : "."),
      );
    } catch (err) {
      setEsito(errMsg(err));
    }
  };

  return (
    <>
      <div className="card space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="font-display text-lg text-fg">
            {ed.nome}{" "}
            <span className="text-xs text-fg-muted font-sans">
              · {etichetta(ed.stato)}
              {ed.di_prova && tr(" · di prova")}
            </span>
          </div>
          <div className="flex gap-2">
            {ed.stato === "in_corso" && (
              <button
                type="button"
                className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
                onClick={() => void onCalcola()}
                disabled={calcola.isPending}
              >
                {calcola.isPending ? tr("Calcolo…") : tr("🧮 Calcola dai risultati di lega")}
              </button>
            )}
            {ed.di_prova && (
              <button
                type="button"
                className="btn-ghost text-xs border border-danger/40 text-danger"
                onClick={async () => {
                  if (!confirm(tr('Eliminare l\'edizione di prova "{{v1}}"?', { v1: ed.nome })))
                    return;
                  await elimina.mutateAsync(ed.id);
                }}
              >
                {tr("Elimina")}
              </button>
            )}
          </div>
        </div>
        <div className="text-[11px] text-fg-muted">
          {tr(
            "Il calcolo legge i fantapunti che ogni squadra ha fatto nella propria lega nelle giornate del turno: va lanciato quando le leghe hanno calcolato quelle giornate. Si può rilanciare quante volte si vuole.",
          )}
        </div>
        {esito && <div className="text-xs text-fg">{esito}</div>}
      </div>
      <Tabellone ed={ed} />
    </>
  );
}
